import {
	wrapper,
	createApiClient,
	Result,
	ApiError,
	PaginationParams,
} from "../base";
import HierarchyDO = App.Data.Hierarchy.HierarchyDefinitionDO;
import HierarchyTreeDO = App.Data.Hierarchy.HierarchyTreeDO;
import HierarchyPostDTO = App.Data.Hierarchy.HierarchyPostDTO;
import HierarchyNodeDO = App.Data.Hierarchy.HierarchyNodeDO;
import HierarchyPatchDTO = App.Data.Hierarchy.HierarchyPatchDTO;
import HierarchyNodePostDTO = App.Data.Hierarchy.HierarchyNodePostDTO;
import HierarchyNodePutAssetDTO = App.Data.Hierarchy.HierarchyNodePutAssetDTO;
import AssetDO = App.Data.Hierarchy.AssetDO;
import HierarchyDefinitionDO = App.Data.Hierarchy.HierarchyDefinitionDO;

const basePath = `hierarchy`;
const baseClient = createApiClient();

type AssetSearchParams = {
	"filter[name]"?: string;
};

type AssetDownloadParams = {
	"filter[type]"?: "unassigned" | "assigned";
};

export default class {
	get(): Promise<Result<Record<string, HierarchyDO[]>, ApiError>> {
		return wrapper(baseClient.get(`${basePath}`));
	}

	syncAssetsFromClickhouse(
		dimension_name_clean: string
	): Promise<Result<string, ApiError>> {
		return wrapper(
			baseClient.get(
				`${basePath}/sync-assets-from-clickhouse/${dimension_name_clean}`
			)
		);
	}

	find(id: number): Promise<Result<HierarchyDO, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/${id}`));
	}

	update(
		id: number,
		data: HierarchyPatchDTO
	): Promise<Result<HierarchyDO, ApiError>> {
		return wrapper(baseClient.patch(`${basePath}/${id}`, data));
	}

	delete(id: number): Promise<Result<void, ApiError>> {
		return wrapper(baseClient.delete(`${basePath}/${id}`));
	}

	create(
		data: HierarchyPostDTO
	): Promise<Result<HierarchyDefinitionDO, ApiError>> {
		return wrapper(baseClient.post(`${basePath}`, data));
	}

	// HIERARCHY NODES
	getTree(id: number): Promise<Result<HierarchyTreeDO, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/${id}/tree`));
	}

	setTree(
		id: number,
		data: HierarchyTreeDO
	): Promise<Result<HierarchyTreeDO, ApiError>> {
		return wrapper(baseClient.put(`${basePath}/${id}/tree`, data));
	}

	downloadAssets(
		id: number,
		params: AssetDownloadParams = {}
	): Promise<Result<HierarchyTreeDO, ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/${id}/assets/download`, { params })
		);
	}

	createNode(
		data: HierarchyNodePostDTO
	): Promise<Result<HierarchyNodeDO, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/nodes/`, data));
	}

	deleteNode(id: number): Promise<Result<HierarchyNodeDO, ApiError>> {
		return wrapper(baseClient.delete(`${basePath}/nodes/${id}`));
	}

	updateNode(
		hierarchyTreeDO: HierarchyTreeDO
	): Promise<Result<HierarchyDO, ApiError>> {
		return wrapper(
			baseClient.patch(
				`${basePath}/nodes/${hierarchyTreeDO.id}`,
				hierarchyTreeDO
			)
		);
	}

	getNodeAssets(id: number): Promise<Result<AssetDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/nodes/${id}/assets`));
	}

	setNodeAssets(
		id: number,
		data: HierarchyNodePutAssetDTO[]
	): Promise<Result<AssetDO[], ApiError>> {
		return wrapper(baseClient.put(`${basePath}/nodes/${id}/assets`, data));
	}

	searchAssetsForNode(
		id: number,
		params: AssetSearchParams = {}
	): Promise<Result<AssetDO[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/nodes/${id}/assets/search`, { params })
		);
	}
}
